import { type IAdvancedRichText } from '../../../../../../inputs/rich-text-advanced/IAdvancedRichText.ts';
import { type IBaseWidget } from '../../../../IBaseWidget.ts';
import {
  type IWidgetComponentProps,
  type IWidgetComponentPropsConfig,
} from '../../../../IWidgetType.ts';
import { hasWidgetText } from '../../boolean/IBooleanWidget.ts';
import { isInputWidget } from '../../isInputWidget.ts';
import { hasWidgetHiddenLabel } from './hasWidgetHiddenLabel.ts';

export interface IWidgetParsedProps {
  label?: IAdvancedRichText;
  hideLabel?: boolean;
  placeholder?: string;
  helpText?: IAdvancedRichText;
  text?: IAdvancedRichText;
  isTableColumn: boolean;
  name: string;
  preview: boolean;
  readonly: boolean;
  value: null | undefined;
}

/**
 * Gestisce le proprietà del componente widget. In questo modo attraverso le props è possibile gestire le proprietà grafiche del widget,
 * come per esempio nascondere parti, in maniera centralizzata e uguale per tutti i widget.
 * @param props
 */
export function getWidgetProps<W extends IBaseWidget>(
  props: IWidgetComponentProps<W>,
): IWidgetParsedProps {
  const { widget, componentLayout, preview, name, readonly } = props;

  if (!preview && !name) {
    // biome-ignore format: <tbd>
    console.error(`[getWidgetProps] name is required when preview is false [${widget.type}]`, { props });
  }

  return {
    ...getInputWidgetProps(widget, componentLayout),
    ...getBooleanWidgetProps(widget, componentLayout),
    isTableColumn: componentLayout?.isTableColumn ?? false,
    name: preview ? 'string' : (name ?? '<[name required but not defined]>'),
    preview: Boolean(preview),
    readonly: Boolean(readonly),
    /** Se ci si trova in modalità preview, permette di impostare il valore dell'input a null così da bloccarne la modifica */
    value: preview ? null : undefined,
  };
}

function getInputWidgetProps(
  widget: IBaseWidget,
  componentLayout: IWidgetComponentPropsConfig | undefined,
) {
  if (isInputWidget(widget)) {
    if (!widget.label) {
      // biome-ignore format: <tbd>
      // console.warn(`[getInputWidgetProps] label is required for a11y [${widget.type}]`, { widget });
    }
    return {
      // label: componentLayout?.hideLabel ? undefined : widget.label,
      hideLabel:
        componentLayout?.hideLabel ?? hasWidgetHiddenLabel(widget) ?? undefined,
      label: widget.label,
      placeholder: componentLayout?.hidePlaceholder
        ? undefined
        : widget.placeholder,
      helpText: componentLayout?.hideHelp ? undefined : widget.helpText,
    };
  }
  return {};
}

function getBooleanWidgetProps(
  widget: IBaseWidget,
  componentLayout: IWidgetComponentPropsConfig | undefined,
) {
  if (hasWidgetText(widget)) {
    if (Boolean(componentLayout?.hideText) || !widget.text) {
      return { text: undefined };
    }

    return {
      text: widget.text,
    };
  }
  return {};
}
